import React, { useEffect, useState, useCallback } from 'react';
import {
  Row,
  Col,
  Form,
  FloatingLabel,
  OverlayTrigger,
  Popover,
  Button
} from 'react-bootstrap';
import DatePicker from 'components/base/DatePicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsDownToLine,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import HVASpinner from 'components/base/HVASpinner';
import { QAProcessorLogTable } from 'components/tables/QAProcessorLogTable';
import {
  getProcessorStatus,
  QAProcessorCall,
  getProcessorConfig,
  QAProcessorConfig
} from 'services/qaprocessor.service';
import { QAProcessorConfigModal } from './QAProcessorConfigModal';

const QAProcessorLog = () => {
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [calls, setCalls] = useState<QAProcessorCall[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<Date[]>([]);
  const [dateRangeOption, setDateRangeOption] = useState('last30');
  const [appliedFilters, setAppliedFilters] = useState({
    startdate: new Date(),
    enddate: new Date()
  });
  const [showPopover, setShowPopover] = useState(false);
  const [shouldBounce, setShouldBounce] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  // eslint-disable-next-line
  const [processorConfig, setProcessorConfig] = useState<QAProcessorConfig | null>(null);


  const getDefaultDateRange = () => {
    const today = new Date();
    const startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    setDateRangeOption('last30');
    return [startDate, today];
  };

  const formatDateRange = (dates: Date[]) => {
    if (dates.length === 2) {
      return `${dates[0].toLocaleDateString()} - ${dates[1].toLocaleDateString()}`;
    }
    return '';
  };

  // Recuperamos la configuración actual del QA Processor
  const fetchConfig = useCallback(async () => {
    try {
      const config = await getProcessorConfig();
      setProcessorConfig(config);
    } catch (error) {
      console.error('Error loading processor config:', error);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Función para mezclar los datos actuales con los nuevos
  const mergeCalls = useCallback(
    (currentCalls: QAProcessorCall[], newCalls: QAProcessorCall[]) => {
      // Si no hay nuevas llamadas, debemos limpiar la tabla
      if (!newCalls.length) {
        return [];
      }

      const mergedMap = new Map<string, QAProcessorCall>();

      // Añadir todas las nuevas llamadas
      newCalls.forEach(newCall => {
        mergedMap.set(newCall.callid, newCall);
      });

      // Revisar llamadas actuales solo para mantener la referencia si no hay cambios
      currentCalls.forEach(currentCall => {
        const newCall = mergedMap.get(currentCall.callid);
        if (newCall) {
          const hasChanges =
            currentCall.status !== newCall.status ||
            currentCall.retrycount !== newCall.retrycount ||
            currentCall.errormessage !== newCall.errormessage ||
            // eslint-disable-next-line
            currentCall.transcriptionstarttime !== newCall.transcriptionstarttime ||
            currentCall.transcriptionendtime !== newCall.transcriptionendtime ||
            // eslint-disable-next-line
            currentCall.summarizationstarttime !== newCall.summarizationstarttime ||
            currentCall.summarizationendtime !== newCall.summarizationendtime ||
            currentCall.analysisstarttime !== newCall.analysisstarttime ||
            currentCall.analysisendtime !== newCall.analysisendtime;

          if (!hasChanges) {
            mergedMap.set(currentCall.callid, currentCall);
          }
        }
      });

      const result = Array.from(mergedMap.values()).sort(
        (a, b) =>
          new Date(b.createdat).getTime() - new Date(a.createdat).getTime()
      );

      return result;
    },
    []
  );

  const fetchCalls = useCallback(
    async (isInitialFetch: boolean = false) => {
      try {
        const response = await getProcessorStatus({
          datefrom: appliedFilters.startdate.toISOString(),
          dateto: appliedFilters.enddate.toISOString()
        });

        if (isInitialFetch) {
          setCalls(response);
          setIsInitialLoading(false);
        } else {
          // Forzar la actualización del estado con los nuevos datos
          setCalls(currentCalls => {
            const merged = mergeCalls(currentCalls, response);
            return merged;
          });
        }

        setError(null);
      } catch (err) {
        setError('Error fetching processor status');
        console.error('Error fetching processor status:', err);
        if (isInitialFetch) {
          setIsInitialLoading(false);
        }
      }
    },
    [appliedFilters, mergeCalls]
  );

  // Actualiza la consulta cada tres segundos
  useEffect(() => {
    const pollData = async () => {
      await fetchCalls(false);
    };

    // Carga inicial
    fetchCalls(true);

    // Configurar el intervalo
    const interval = setInterval(pollData, 3000);

    // Limpiar el intervalo cuando cambien los filtros o se desmonte
    return () => {
      clearInterval(interval);
    };
  }, [appliedFilters, fetchCalls]);

  // Cargar el intervalo de tiempo por defecto
  useEffect(() => {
    const defaultRange = getDefaultDateRange();
    setSelectedDateRange(defaultRange);
    setAppliedFilters({
      startdate: defaultRange[0],
      enddate: defaultRange[1]
    });
  }, []);

  const handleDateChange = (date: Date[]) => {
    if (date.length === 2) {
      setSelectedDateRange(date);
      setShowPopover(false);
      setShouldBounce(true);
    }
  };

  const applyFilters = () => {
    setAppliedFilters({
      startdate: selectedDateRange[0],
      enddate: selectedDateRange[1]
    });
    setShouldBounce(false);
  };

  const handleDateRangeOptionChange = (value: string) => {
    setDateRangeOption(value);
    let startDate: Date, endDate: Date;
    const today = new Date();

    switch (value) {
      case 'last30':
        startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        endDate = today;
        break;
      case 'last3months':
        startDate = new Date(
          today.getFullYear(),
          today.getMonth() - 3,
          today.getDate()
        );
        endDate = today;
        break;
      case 'thisyear':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = today;
        break;
      default:
        return;
    }

    setSelectedDateRange([startDate, endDate]);
    setShowPopover(false);
    setShouldBounce(true);
  };

  const datePopover = (
    <Popover id="date-range-popover" className="shadow-lg lh-sm fs-9">
      <Popover.Body>
        <p>Select a date range option:</p>
        <div className="mb-3">
          {['last30', 'last3months', 'thisyear', 'custom'].map(option => (
            <div key={option} className="form-check form-switch mb-0">
              <label className="d-flex fs-9 justify-content-start align-items-center mx-2">
                <input
                  type="checkbox"
                  value={option}
                  checked={dateRangeOption === option}
                  onChange={() => handleDateRangeOptionChange(option)}
                  className="form-check-input pt-0"
                />
                <div className="fs-9 fw-semibold pt-1 px-2">
                  {option === 'last30' && 'Last 30 days'}
                  {option === 'last3months' && 'Last 3 months'}
                  {option === 'thisyear' && 'This year'}
                  {option === 'custom' && 'Custom range'}
                </div>
              </label>
            </div>
          ))}
        </div>
        {dateRangeOption === 'custom' && (
          <>
            <p>Select a custom date range from the date picker below:</p>
            <DatePicker
              value={selectedDateRange}
              onChange={handleDateChange}
              options={{
                dateFormat: 'M j, Y',
                mode: 'range',
                onClose: () => setShowPopover(false)
              }}
            />
          </>
        )}
      </Popover.Body>
    </Popover>
  );

  return (
    <>
      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 pt-0 pb-0 mt-0 mb-0">
        <Row className="g-0">
          <Col xs={12} lg={8} className="px-0">
            <Row className="gy-3 mb-1 justify-content-between">
              <Col>
                <h2 className="mb-2 text-body-emphasis">
                  QA Processor Management and Activity Log
                </h2>
              </Col>
            </Row>
            <Row className="mb-1 mx-0 p-0 justify-content-start align-items-center">
              <div className="text-start px-0 pt-2">
                <label className="form-label px-2 text-primary">
                  Date range filters
                </label>
              </div>
              <div className="d-flex flex-wrap pb-2">
                <Row>
                  <Col className="p-0 me-1">
                    <FloatingLabel controlId="dateRange" label="Date Range">
                      <OverlayTrigger
                        trigger="click"
                        placement="bottom-start"
                        overlay={datePopover}
                        rootClose={false}
                        show={showPopover}
                        onToggle={nextShow => setShowPopover(nextShow)}
                      >
                        <Form.Control
                          type="text"
                          value={formatDateRange(selectedDateRange)}
                          readOnly
                          style={{ width: '185px' }}
                        />
                      </OverlayTrigger>
                    </FloatingLabel>
                  </Col>
                  <Col className="d-flex justify-content-start align-items-center">
                    <Button
                      variant="link"
                      onClick={applyFilters}
                      className="px-1 pt-4"
                    >
                      <FontAwesomeIcon
                        icon={faArrowsDownToLine}
                        size="xl"
                        bounce={shouldBounce}
                      />
                    </Button>
                  </Col>
                </Row>
              </div>
            </Row>
          </Col>
          <Col xs={12} lg={4} className="px-0 pb-4">
            <div className="border rounded bg-light dark__bg-gray-1100 h-100 fs-10 p-3 pt-2 pb-2">
              <Row>
                <Col xs="auto" xxl="auto">
                  <h5 className="text-body-highlight fw-bold mb-1 pt-1">
                    QA Processor system
                  </h5>
                  <Button
                    variant="link"
                    className="p-0 text-decoration-none fs-10"
                    onClick={() => setShowConfigModal(true)}
                  >
                    Status:{' '}
                    {processorConfig ? (
                      <span>
                        {processorConfig.active ? 'Active' : 'Inactive'}
                      </span>
                    ) : (
                      'Loading...'
                    )}
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className="px-1 pe-0"
                    />
                  </Button>
                  {processorConfig && (
                    <QAProcessorConfigModal
                      show={showConfigModal}
                      onHide={() => setShowConfigModal(false)}
                      config={processorConfig}
                      onConfigUpdate={fetchConfig}
                    />
                  )}
                </Col>
              </Row>
              {processorConfig && (
                <>
                  <h6 className="text-body-highlight pt-2">Configuration</h6>
                  <div>
                    <div className="mb-0 lh-1">
                      <span className="fw-bold">
                        Transcription (Main → Fallback):
                      </span>{' '}
                      {processorConfig.transcriptionMainProvider} →{' '}
                      {processorConfig.transcriptionFallbackProvider}
                      <br />
                      <span className="fw-bold">
                        Analysis (Main → Fallback):
                      </span>{' '}
                      {processorConfig.analysisMainProvider} →{' '}
                      {processorConfig.analysisFallbackProvider}
                      <br />
                      <span className="fw-bold">
                        Concurrent calls to analyze:
                      </span>{' '}
                      {processorConfig.maxConcurrentProcesses}
                    </div>
                  </div>
                </>
              )}
            </div>
          </Col>
        </Row>
      </div>
      <div
        id="Content"
        className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top"
      >
        {isInitialLoading ? (
          <HVASpinner />
        ) : error ? (
          <div className="text-danger">{error}</div>
        ) : (
          <QAProcessorLogTable calls={calls} />
        )}
      </div>
    </>
  );
};

export default QAProcessorLog;
